<template>
  <div class="project-single">
    <app-view-layout :header="commercialPrint.name">
      <template #toolbarMenu>
        <SingleCommercialPrintToolbarMenu
          :commercial-print="commercialPrint"
          :actions="actions"
          @delete="confirmCommercialPrintDelete"
        />
      </template>

      <template #content>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <CommercialPrintDetailsCard :commercial-print="commercialPrint" />
            <ProjectChangesListCard
              v-if="commercialPrint.history"
              class="mt-8"
              :history="commercialPrint.history"
              commercial-print
            />
          </v-col>

          <v-col
            cols="12"
            md="8"
          >
            <v-row>
              <v-col cols="12">
                <ProjectStatusCard
                  :project="commercialPrint"
                  disable-status-change
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <ProjectTeamCard
                  :users="commercialPrint.members"
                  title="Zespół projektowy"
                  :editable="teamEditPermitted"
                  @add="showAddTeamMemberDialog('members')"
                  @delete="deleteUser($event, 'members')"
                />
              </v-col>

              <v-col>
                <ProjectTeamCard
                  :users="commercialPrint.orderers"
                  title="Zespół zlecający"
                  empty-state-text="Oczekiwanie na dodanie członków zespołu zlecającego."
                  :editable="teamEditPermitted"
                  @add="showAddTeamMemberDialog('orderers')"
                  @delete="deleteUser($event, 'orderers')"
                />
              </v-col>

              <v-col>
                <ProjectTeamCard
                  :users="commercialPrint.managers"
                  title="Zespół decyzyjny"
                  empty-state-text="Oczekiwanie na dodanie członków zespołu decyzyjnego."
                  :editable="teamEditPermitted"
                  @add="showAddTeamMemberDialog('managers')"
                  @delete="deleteUser($event, 'managers')"
                />
              </v-col>

              <v-col
                cols="12"
                :order="user.layoutType === 'graphic_designer_layout' ? 'first' : undefined"
              >
                <ProjectFilesCard
                  v-if="commercialPrint.files"
                  ref="commercialPrintFiles"
                  :editable="commercialPrint.editable"
                  :disable-addition="!commercialPrint.editable"
                  :disable-delete="!commercialPrint.editable"
                  thumbnail
                  hide-tabs
                  commercial-print-files
                  :files="commercialPrint.files"
                  :processing="isProcessing"
                  @add="showFileUploadDialog"
                  @delete="deleteFiles"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <app-text-card
              v-if="commercialPrint.description !== null"
              :value="commercialPrint.description"
              title="Opis projektu"
              disabled
            >
              <AppEmptyState
                title="Brak opisu projektu"
                subtitle="Oczekiwanie na dodanie opisu projektu."
                class="pb-16"
              />
            </app-text-card>
          </v-col>
        </v-row>
      </template>
    </app-view-layout>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import AppViewLayout from '@/components/Layout/AppViewLayout.vue';
import SingleCommercialPrintToolbarMenu from '@/components/CommercialPrint/SingleCommercialPrintToolbarMenu.vue';
import CommercialPrintDetailsCard from '@/components/CommercialPrint/CommercialPrintDetailsCard.vue';
import ProjectTeamCard from '@/components/Projects/ProjectTeamCard.vue';
import ProjectChangesListCard from '@/components/Projects/Changes/ProjectChangesListCard.vue';
import ProjectFilesCard from '@/components/Projects/ProjectFilesCard.vue';
import ProjectStatusCard from '@/components/Projects/ProjectStatusCard.vue';
import AppEmptyState from '@/components/UI/AppEmptyState.vue';
import navigateBackMixin from '@/helpers/navigateBackMixin';
import AppTextCard from '@/components/UI/AppTextCard.vue';
import commercialPrint from '../../store/modules/commercialPrint';

export default {
  components: {
    AppViewLayout,
    SingleCommercialPrintToolbarMenu,
    CommercialPrintDetailsCard,
    ProjectTeamCard,
    ProjectChangesListCard,
    ProjectFilesCard,
    ProjectStatusCard,
    AppEmptyState,
    AppTextCard,
  },

  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },

  mixins: [navigateBackMixin], // prevRoute

  computed: {
    ...mapState('commercialPrintSingle', ['commercialPrint', 'isProcessing', 'actions', 'changes']),
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['checkUserPermissions']),

    teamEditPermitted() {
      const availability = ['commercial_print_graphic_coordinator', 'admin_director', 'chief'];
      const isOwner = this.user.id === this.commercialPrint.userId;
      return isOwner || this.checkUserPermissions(availability); // sprawdza czy user ma którąś z tych ról
    },
  },

  created() {
    this.fetchSingleCommercialPrint(this.id).then(() => {
      this.fetchCommercialPrintActions(this.id);
    });
  },

  beforeDestroy() {
    this.resetCommercialPrint();
  },

  methods: {
    ...mapActions('commercialPrintSingle', [
      'fetchSingleCommercialPrint',
      'deleteCommercialPrintFiles',
      'resetCommercialPrint',
      'fetchCommercialPrintActions',
      'deleteCommercialPrint',
      'createCommercialPrintUser',
      'deleteCommercialPrintUser',
    ]),

    ...mapActions('dialog', ['showDialog', 'clearDialog']),

    deleteFiles(fileIds) {
      this.deleteCommercialPrintFiles(fileIds).then(() => {
        this.$refs.commercialPrintFiles.clearSelection();
        this.commercialPrint.files = this.commercialPrint.files.filter((file) => !fileIds.includes(file.id));
      });
    },

    deleteUser(userId, listType) {
      this.deleteCommercialPrintUser({ id: userId, listType });
    },

    confirmCommercialPrintDelete() {
      const config = {
        header: 'Trwałe usuwanie akcydensu',
        text: 'Czy na pewno chcesz trwale usunąć akcydens?',
        props: {
          confirmText: 'Usuń',
          rejectText: 'Nie usuwaj',
        },
      };
      this.callConfirmDialog(config, this.deleteCommercialPrint).then(() => {
        this.$router.push({ name: 'commercial-print-index' });
      });
    },

    callConfirmDialog(config, callback) {
      return new Promise((resolve) => {
        this.showDialog({
          component: 'Confirm',
          ...config,
        })
          .then(() => {
            callback(this.id).then(() => {
              resolve();
            });
          })
          .catch(() => {
            this.clearDialog();
          });
      });
    },

    showFileUploadDialog() {
      this.showDialog({
        header: 'Dodawanie pliku',
        text: 'Wybierz plik i dodaj do projektu.',
        component: 'FileUpload',
        props: {
          commercialPrintFile: true,
          callback: () => this.fetchSingleCommercialPrint(this.id),
        },
      });
    },

    showAddTeamMemberDialog(teamType) {
      this.showDialog({
        header: 'Dodaj członka zespołu',
        text: 'W poniszym polu wybierz osobę której wysłać zaproszenie',
        component: 'AddCPTeamMember',
        props: {
          type: teamType,
        },
      });
    },
  },
};
</script>
